import React from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { LayoutDashboard, Store, CalendarCheck2, LogOut, FileText, Star, BarChart3, Stethoscope, Package } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const LOGO_URL =
    "https://www.magniflex.com/frontend_assets/b2c/media/img/logo/magniflex-official-logo-it_IT.png";

export default function AdminLayout() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-magni-cream text-magni-muted">
                Caricamento...
            </div>
        );
    }
    if (!user) return <Navigate to="/admin/login" replace />;

    if (user.role !== "admin") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-magni-cream px-4">
                <div className="max-w-md w-full bg-white border border-magni-light p-10 text-center">
                    <div className="overline mb-2" style={{ color: "#dc3545" }}>Accesso non consentito</div>
                    <h1 className="font-serif-display text-2xl text-magni-navy mb-3">
                        Quest'area è riservata agli amministratori
                    </h1>
                    <p className="text-sm text-magni-muted mb-6">
                        Sei loggato come <strong>{user.email}</strong> (rivenditore).
                        Per accedere al backoffice esci e accedi con un account amministratore.
                    </p>
                    <div className="flex gap-3 justify-center">
                        <button
                            onClick={() => navigate("/rivenditore")}
                            className="magni-btn-secondary"
                            style={{ padding: "10px 18px", fontSize: "12px" }}
                        >
                            Area rivenditore
                        </button>
                        <button
                            onClick={async () => { await logout(); navigate("/admin/login"); }}
                            className="magni-btn-primary"
                            style={{ padding: "10px 18px", fontSize: "12px" }}
                        >
                            Esci e accedi come admin
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const handleLogout = async () => {
        await logout();
        navigate("/admin/login");
    };

    const linkClass = ({ isActive }) =>
        `flex items-center gap-3 px-5 py-3 text-sm transition-all ${
            isActive
                ? "bg-white/10 text-white border-l-2 border-[#e8f1f5]"
                : "text-white/70 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
        }`;

    return (
        <div className="min-h-screen flex bg-magni-cream">
            {/* Sidebar */}
            <aside
                data-testid="admin-sidebar"
                className="w-64 bg-magni-navy text-white flex flex-col flex-shrink-0 sticky top-0 h-screen"
            >
                <div className="p-6 border-b border-white/10">
                    <img
                        src={LOGO_URL}
                        alt="Magniflex"
                        className="h-10 w-auto"
                        style={{ filter: "brightness(0) invert(1)" }}
                    />
                    <div className="overline mt-3" style={{ color: "#e8f1f5" }}>
                        Backoffice
                    </div>
                </div>
                <nav className="flex-1 py-4">
                    <NavLink to="/admin" end className={linkClass} data-testid="nav-dashboard">
                        <LayoutDashboard className="w-4 h-4" />
                        Dashboard
                    </NavLink>
                    <NavLink to="/admin/stores" className={linkClass} data-testid="nav-stores">
                        <Store className="w-4 h-4" />
                        Negozi
                    </NavLink>
                    <NavLink to="/admin/prodotti" className={linkClass} data-testid="nav-products">
                        <Package className="w-4 h-4" />
                        Prodotti
                    </NavLink>
                    <NavLink to="/admin/bookings" className={linkClass} data-testid="nav-bookings">
                        <CalendarCheck2 className="w-4 h-4" />
                        Prenotazioni
                    </NavLink>
                    <NavLink to="/admin/feedback" className={linkClass} data-testid="nav-feedback">
                        <Star className="w-4 h-4" />
                        Feedback
                    </NavLink>
                    <NavLink to="/admin/campagne" className={linkClass} data-testid="nav-campaigns">
                        <BarChart3 className="w-4 h-4" />
                        Campagne UTM
                    </NavLink>
                    <NavLink to="/admin/contenuti" className={linkClass} data-testid="nav-content">
                        <FileText className="w-4 h-4" />
                        Testi home
                    </NavLink>
                    <NavLink to="/admin/diagnostica" className={linkClass} data-testid="nav-diagnostics">
                        <Stethoscope className="w-4 h-4" />
                        Diagnostica
                    </NavLink>
                </nav>
                <div className="p-4 border-t border-white/10 text-xs text-white/60">
                    <div className="mb-3">
                        <div className="text-white/90 truncate" data-testid="admin-user-email">
                            {user.email}
                        </div>
                    </div>
                    <button
                        data-testid="admin-logout-btn"
                        onClick={handleLogout}
                        className="flex items-center gap-2 text-white/80 hover:text-white"
                    >
                        <LogOut className="w-4 h-4" /> Esci
                    </button>
                </div>
            </aside>

            <main className="flex-1 min-w-0 p-6 sm:p-10 overflow-x-auto">
                <Outlet />
            </main>
        </div>
    );
}
